import {useCallback, useEffect, useState} from 'react';
import type {
  FormDefinitionResponse,
  FormResponse,
} from 'src/api/ProactApiClient';
import {FormStateEnum} from 'src/api/ProactApiClient';
import {ApiEndpoints} from 'src/shared/utils/ApiCaller';
import {useStateMachine} from 'src/shared/hooks/api/useStateMachine';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useLazyGetFormQuery} from 'src/redux/queries/form';
import {useLazyGetFormDefinitionVersionQuery} from 'src/redux/queries/formDefinition';
import type {IFormData} from 'src/types/Form';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';
import type {IStateMachine} from 'src/types/StateMachine';
import {tryParse} from 'src/utilities/objectUtils';
import {identifierFieldName} from 'src/types/FormV2';
import {mapFormDefinitionResponseToFormFieldsV2} from '../formHelpers';
import {
  normalizeFormDefinitionSubFormFieldIds,
  normalizeFormFieldValues,
  resolveQualifiedFieldValueName,
} from 'src/types/formFieldQualifiedName';

export interface UseFormSubmissionViewerOptions {
  formDefinitionId: string | number;
  formSubmissionId: string | number;
}

export interface UseFormSubmissionViewerResult {
  form: FormResponse | undefined;
  formDefinition: FormDefinitionResponse | undefined;
  formData: IFormData;
  formMeta: IFormFieldV2[];
  formReferenceImage: string | undefined;
  isLoading: boolean;
  stateMachine: IStateMachine | undefined;
  isMachineLoading: boolean;
  canEdit: boolean;
  /** Re-fetch the form submission data (e.g., after a transition). */
  refetch: () => void | Promise<unknown>;
}

export const useFormSubmissionViewer = ({
  formDefinitionId,
  formSubmissionId,
}: UseFormSubmissionViewerOptions): UseFormSubmissionViewerResult => {
  const {setAlert} = useAlert();

  const defId = String(formDefinitionId);
  const subId = String(formSubmissionId);

  const [formDefinition, setFormDefinition] = useState<
    FormDefinitionResponse | undefined
  >();
  const [form, setForm] = useState<FormResponse | undefined>();
  const [formVersion, setFormVersion] = useState<number | undefined>();

  const [formData, setFormData] = useState<IFormData>({});
  const [formMeta, setFormMeta] = useState<IFormFieldV2[]>([]);

  const [isFormLoading, setIsFormLoading] = useState(true);
  const [isDefinitionLoading, setIsDefinitionLoading] = useState(true);
  const isLoading = isFormLoading || isDefinitionLoading;

  const [formReferenceImage, setFormReferenceImage] = useState<
    string | undefined
  >();

  const [machine, isMachineLoading] = useStateMachine({
    url: `${ApiEndpoints.getStaticForm}/state-machine`,
  });

  const [triggerGetForm] = useLazyGetFormQuery();
  const [triggerGetFormDefinitionVersion] =
    useLazyGetFormDefinitionVersionQuery();

  // Load form definition with version resolution
  const getDefinition = useCallback(async () => {
    if (defId === undefined) return;
    if (subId !== undefined && formVersion === undefined) return;

    setIsDefinitionLoading(true);
    try {
      const response = await triggerGetFormDefinitionVersion({
        id: parseInt(defId),
        version: formVersion ?? 0,
      }).unwrap();
      const normalizedResponse =
        normalizeFormDefinitionSubFormFieldIds(response);
      setFormDefinition(normalizedResponse);
      const fields = mapFormDefinitionResponseToFormFieldsV2(
        normalizedResponse,
        false
      );
      setFormMeta(fields);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsDefinitionLoading(false);
  }, [defId, formVersion]);

  useEffect(() => {
    void getDefinition();
  }, [defId, formVersion]);

  // Load form submission
  const getForm = useCallback(async () => {
    try {
      if (subId === undefined) return;
      setIsFormLoading(true);

      const response = await triggerGetForm(parseInt(subId)).unwrap();
      setFormVersion(response.formDefinition?.version);
      setForm({
        ...response,
        fieldValues: normalizeFormFieldValues(response.fieldValues),
      });

      try {
        const img = tryParse(
          tryParse(response.metadata?.cache)?.referenceImage
        )[0].value;
        setFormReferenceImage(img);
      } catch {
        /* no reference image */
      }
    } catch (error) {
      console.error(error);
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsFormLoading(false);
  }, [subId]);

  useEffect(() => {
    void getForm();
  }, [subId]);

  useEffect(() => {
    if (!form || !formDefinition) return;

    const data: IFormData = {};
    data[identifierFieldName] = form.shortDescription;
    const fieldTypeByName = new Map(formMeta.map(f => [f.name, f.type]));
    form.fieldValues?.forEach(fieldValue => {
      const name = resolveQualifiedFieldValueName(fieldValue, formDefinition);
      if (fieldTypeByName.get(name) === 'select-multiple') {
        const selected = (data[name] as ISelectOption[] | undefined) ?? [];
        data[name] = fieldValue.value
          ? [
              ...selected,
              {id: fieldValue.value, label: fieldValue.displayValue ?? ''},
            ]
          : selected;
      } else {
        data[name] = fieldValue.value;
      }
    });
    setFormData(data);
  }, [form, formDefinition, formMeta]);

  const canEdit =
    (form?.state === FormStateEnum.Draft ||
      !!form?.formDefinition?.accessAllowEditAfterPublish) &&
    !form?.parentId;

  return {
    form,
    formDefinition,
    formData,
    formMeta,
    formReferenceImage,
    isLoading,
    stateMachine: machine,
    isMachineLoading,
    canEdit,
    refetch: getForm,
  };
};
